/**
 * Per-scenario background line-art motif (Option A).
 *
 * A single, large, very faint Lucide icon is drawn behind each scenario to evoke its subject. Lucide is
 * already the project's icon system, so all motifs share one stroke style and theme automatically (brand
 * rule: Lucide line icons, no emoji). The art is purely decorative — rendered aria-hidden and kept faint /
 * in the bottom-right negative space (see components/ScenarioArt.tsx) so it never affects text contrast.
 * (WCAG exempts decorative imagery from contrast, but we keep prose readability intact regardless.)
 *
 * The CLOSE-09 prioritisation pop-up is a modal interrupt, not a page, so it has no entry here.
 */
import {
  Banknote,
  CalendarClock,
  FileClock,
  FileSearch,
  FileSignature,
  Files,
  Hourglass,
  IdCard,
  Lightbulb,
  Mail,
  MapPin,
  PenLine,
  Plane,
  Receipt,
  ScrollText,
  ShieldCheck,
  Stamp,
  Unlock,
  Users,
  Vault,
  type LucideIcon,
} from "lucide-react";

/** scenarioId → the line-art motif drawn faintly behind that scenario. */
export const SCENARIO_ART: Record<string, LucideIcon> = {
  "SIGN-01": IdCard, // the missing passport — a specimen identity document
  "SIGN-02": Plane, // a director abroad (the Maldives)
  "SIGN-03": FileSignature, // DocuSign or Bank ID — electronic signing
  "SIGN-04": CalendarClock, // two notice periods that don't fit
  "SIGN-05": Hourglass, // five days you don't have
  "SIGN-06": FileSearch, // an unfinished diligence report
  "SIGN-07": Mail, // telling Swedish counsel
  "SIGN-08": PenLine, // a signature that doesn't match
  "CLOSE-01": ScrollText, // the mortgage notes the old lender won't release
  "CLOSE-02": MapPin, // physical security 700 km away
  "CLOSE-03": Files, // paper notes vs the electronic register
  "CLOSE-04": Receipt, // transfer tax before the register
  "CLOSE-05": Vault, // what does escrow mean?
  "CLOSE-06": Unlock, // effective on release, not on receipt
  "CLOSE-07": Users, // boards that can't yet act
  "CLOSE-08": Lightbulb, // a stray IPR qualification
  "CLOSE-10": FileClock, // the memorandum that can't come first
  "CLOSE-11": Banknote, // funds in flight
  "CLOSE-12": Stamp, // an endorsement by the wrong hand
  "POST-01": ShieldCheck, // perfection — the deal isn't done until it's perfected
};
